import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader } from "rsuite";

import PrescriberLayout from "../../components/PrescriberLayout";
import { useConfig } from "../../context/config-context";

/**
 * DocumentViewerPage - Page publique d'affichage d'un document PDF
 *
 * Route: /prescriber-access/:permanentToken/view/:documentId?sessionToken=...
 *
 * - Affiche le PDF en inline si possible
 * - Affiche une page d'erreur propre si fichier introuvable / accès refusé
 */
export default function DocumentViewerPage() {
  const { permanentToken, documentId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionToken = searchParams.get("sessionToken");
  const siteConfig = useConfig();

  const primaryColor = siteConfig.cssVariables?.["--primary"] || "#24335d";

  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [errorCode, setErrorCode] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);

  useEffect(() => {
    if (!permanentToken || !documentId || !sessionToken) {
      setErrorCode("MISSING_AUTH");
      setStatus("error");
      return;
    }

    const apiBase = siteConfig.api?.baseUrl || "";
    const url = `${apiBase}/prescriber-access/${permanentToken}/view/${documentId}?sessionToken=${sessionToken}`;

    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setErrorCode(data?.error?.code || "FILE_NOT_FOUND");
          setStatus("error");
          return;
        }
        const contentType = res.headers.get("content-type") || "";
        const blob = await res.blob();
        if (contentType.includes("application/pdf")) {
          setFileUrl(URL.createObjectURL(blob));
          setStatus("ok");
        } else {
          // Non-PDF : déclencher le téléchargement directement
          const disposition = res.headers.get("content-disposition") || "";
          const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
          const filename = filenameMatch?.[1] || `document-${documentId}`;
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setStatus("downloaded");
        }
      })
      .catch(() => {
        setErrorCode("SERVER_ERROR");
        setStatus("error");
      });
  }, [permanentToken, documentId, sessionToken, siteConfig]);

  // ─── Messages d'erreur selon le code ───────────────────────────────────────
  const ERROR_MESSAGES = {
    FILE_NOT_FOUND: {
      title: "Document introuvable",
      description: "Ce document n'est pas disponible sur le serveur. Il est possible qu'il n'ait pas encore été synchronisé.",
    },
    ACCESS_DENIED: {
      title: "Accès refusé",
      description: "Vous n'êtes pas autorisé à consulter ce document.",
    },
    SESSION_NOT_FOUND: {
      title: "Session expirée",
      description: "Votre session a expiré. Veuillez retourner sur la page de liste et vous reconnecter.",
    },
    SESSION_EXPIRED: {
      title: "Session expirée",
      description: "Votre session a expiré. Veuillez retourner sur la page de liste et vous reconnecter.",
    },
    MISSING_AUTH: {
      title: "Accès non autorisé",
      description: "Les informations d'authentification sont manquantes.",
    },
  };

  const errorInfo = ERROR_MESSAGES[errorCode] || {
    title: "Une erreur est survenue",
    description: "Impossible d'afficher ce document. Veuillez réessayer ou contacter le support.",
  };

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <PrescriberLayout>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <Loader size="md" />
          <p className="text-sm" style={{ color: primaryColor }}>
            Chargement du document…
          </p>
        </div>
      </PrescriberLayout>
    );
  }

  // ─── Téléchargement déclenché (non-PDF) ─────────────────────────────────────
  if (status === "downloaded") {
    return (
      <PrescriberLayout>
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <p className="text-base font-semibold" style={{ color: primaryColor }}>
            Téléchargement démarré
          </p>
          <p className="text-sm text-gray-500">
            Le document a été téléchargé sur votre appareil.
          </p>
          <button
            onClick={() => window.close()}
            className="mt-2 px-4 py-2 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-80"
            style={{ backgroundColor: primaryColor }}
          >
            Fermer
          </button>
        </div>
      </PrescriberLayout>
    );
  }

  // ─── Erreur ─────────────────────────────────────────────────────────────────
  if (status === "error") {
    return (
      <PrescriberLayout>
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <p className="text-base font-semibold" style={{ color: primaryColor }}>
            {errorInfo.title}
          </p>
          <p className="text-sm text-gray-500 max-w-sm">
            {errorInfo.description}
          </p>
          <button
            onClick={() => window.close()}
            className="mt-2 px-4 py-2 text-sm font-semibold rounded-lg transition-opacity hover:opacity-80"
            style={{ backgroundColor: primaryColor, color: "#fff" }}
          >
            Fermer
          </button>
        </div>
      </PrescriberLayout>
    );
  }

  // ─── PDF inline ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: "#F6F7F9" }}>
      <iframe
        src={fileUrl}
        title="Document"
        className="flex-1 w-full border-0"
        style={{ minHeight: "calc(100vh - 0px)" }}
      />
    </div>
  );
}
